import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MainNav from "@/components/MainNav";
import { getProduct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { FaShoppingCart, FaArrowLeft } from "react-icons/fa";

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await getProduct(productId);
        setProduct(res.data);
        // Set default size
        if (res.data?.sizes?.length > 0) {
          const availableSize = res.data.sizes.find(s => s.stock > 0);
          if (availableSize) setSelectedSize(availableSize.size);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        toast.error("Product not found");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(price);
  };

  const getStockForSize = (size) => {
    const variant = product?.sizes?.find(s => s.size === size);
    return variant?.stock || 0;
  };

  const addToCart = () => {
    if (!selectedSize) {
      toast.error("Please select a size");
      return;
    }

    const stock = getStockForSize(selectedSize);
    if (stock < quantity) {
      toast.error("Not enough stock available");
      return;
    }

    // Get existing cart from localStorage
    const existingCart = JSON.parse(localStorage.getItem("cart") || "[]");
    
    // Check if item already in cart
    const existingIndex = existingCart.findIndex(
      item => item.product_id === product.id && item.size === selectedSize
    );

    if (existingIndex >= 0) {
      existingCart[existingIndex].quantity += quantity;
    } else {
      existingCart.push({
        product_id: product.id,
        name: product.name,
        price: product.price,
        size: selectedSize,
        quantity: quantity,
        image: product.images?.[0] || ""
      });
    }

    localStorage.setItem("cart", JSON.stringify(existingCart));
    toast.success("Added to cart!");
    
    // Trigger cart update event
    window.dispatchEvent(new Event("cartUpdated"));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNav />
        <main className="pt-24 pb-16">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12">
              <Skeleton className="aspect-square rounded-lg" />
              <div className="space-y-4">
                <Skeleton className="h-10 w-3/4" />
                <Skeleton className="h-8 w-1/4" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <MainNav />
        <main className="pt-24 pb-16">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p className="text-zinc-500">Product not found</p>
            <Button onClick={() => navigate("/shop")} className="mt-4">
              Back to Shop
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="product-detail-page">
      <MainNav />

      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4">
          <button
            onClick={() => navigate("/shop")}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          >
            <FaArrowLeft /> Back to Shop
          </button>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Product Images */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-4"
            >
              {/* Main Image */}
              <div className="aspect-square rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800">
                {product.images?.[selectedImage] ? (
                  <img
                    src={product.images[selectedImage]}
                    alt={product.name}
                    className="w-full h-full object-contain p-8"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-6xl font-syne text-zinc-700">
                      {product.name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Thumbnail Gallery */}
              {product.images?.length > 1 && (
                <div className="flex gap-3">
                  {product.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index 
                          ? "border-white" 
                          : "border-zinc-700 hover:border-zinc-500"
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} view ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <h1 className="text-3xl md:text-4xl font-syne font-bold text-white mb-2">
                  {product.name}
                </h1>
                <p className="text-2xl text-zinc-300">{formatPrice(product.price)}</p>
              </div>

              <p className="text-zinc-400 leading-relaxed">
                {product.description}
              </p>

              {/* Size Selection */}
              {product.sizes?.length > 0 && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-3">Size</label>
                  <div className="flex flex-wrap gap-3">
                    {product.sizes.map((variant) => (
                      <button
                        key={variant.size}
                        onClick={() => variant.stock > 0 && setSelectedSize(variant.size)}
                        disabled={variant.stock === 0}
                        className={`px-4 py-2 rounded-lg border transition-all ${
                          selectedSize === variant.size
                            ? "border-white bg-white text-black"
                            : variant.stock > 0
                            ? "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                            : "border-zinc-800 text-zinc-600 cursor-not-allowed line-through"
                        }`}
                        data-testid={`size-${variant.size}`}
                      >
                        {variant.size}
                      </button>
                    ))}
                  </div>
                  {selectedSize && (
                    <p className="text-sm text-zinc-500 mt-2">
                      {getStockForSize(selectedSize)} in stock
                    </p>
                  )}
                </div>
              )}

              {/* Quantity */}
              <div>
                <label className="block text-sm text-zinc-400 mb-3">Quantity</label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-500 transition-colors"
                  >
                    -
                  </button>
                  <span className="text-xl text-white w-8 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(getStockForSize(selectedSize), quantity + 1))}
                    className="w-10 h-10 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-500 transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart */}
              <Button
                onClick={addToCart}
                disabled={!selectedSize || getStockForSize(selectedSize) === 0}
                className="w-full py-6 text-lg bg-white text-black hover:bg-zinc-200 rounded-full font-medium"
                data-testid="add-to-cart-btn"
              >
                <FaShoppingCart className="mr-2" />
                Add to Cart - {formatPrice(product.price * quantity)}
              </Button>

              {/* Shipping Info */}
              <div className="border-t border-zinc-800 pt-6">
                <p className="text-sm text-zinc-500">
                  International shipping available. Shipping costs calculated at checkout.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <footer className="border-t border-zinc-800 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-zinc-600 text-xs">© 2025 Underplay. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
